import { PRODUCT_NAME } from '../../constants/brand';
import React from 'react';
import { Box, Chip, Typography, Button, CircularProgress, Avatar, Stack } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import OnboardingStepper, { ONBOARDING_STEPS } from './OnboardingStepper.js';

const TEAL = '#29C0BB';

/**
 * Shared page chrome for every onboarding step, matching the design mockups:
 * top bar (logo + avatar), horizontal stepper, one white content card split
 * into a left sidebar (step chip, title, description, extras) and the step's
 * form area, then a Skip / Continue footer and a privacy note.
 */
const OnboardingLayout = ({
  step,
  title,
  description,
  sidebarExtra,
  children,
  avatarInitials,
  onBack,
  onSkip,
  skipLabel = 'Skip for now',
  skipCaption,
  onContinue,
  onStepClick,
  continueLabel = 'Continue',
  continueDisabled = false,
  saving = false,
  privacyNote = `Your information is private and secure. It will only be used to improve your experience on ${PRODUCT_NAME}.`,
}) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f6f8f9', pb: 6 }}>
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
        }}
      >
        <BrandLogo onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        <Avatar sx={{ bgcolor: '#B9F4F2', color: '#1D8A87', fontSize: 14, fontWeight: 700 }}>
          {avatarInitials || ''}
        </Avatar>
      </Box>

      <OnboardingStepper currentStep={step} onStepClick={onStepClick} />

      {/* Content card */}
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          mt: 3,
          px: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(44, 62, 80, 0.06)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            overflow: 'hidden',
          }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              width: { xs: '100%', md: 320 },
              flexShrink: 0,
              p: { xs: 3, md: 4 },
              borderRight: { md: '1px solid #eef1f4' },
            }}
          >
            <Chip
              label={`Step ${step} of ${ONBOARDING_STEPS.length}`}
              size="small"
              sx={{ backgroundColor: '#DFF7F6', color: '#1D8A87', fontWeight: 600, mb: 2 }}
            />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1c2b36', fontSize: { xs: 26, md: 32 }, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {description && (
              <Typography variant="body1" sx={{ mt: 2, color: '#5b6770' }}>
                {description}
              </Typography>
            )}
            {sidebarExtra && <Box sx={{ mt: 3 }}>{sidebarExtra}</Box>}
          </Box>

          {/* Step content */}
          <Box sx={{ flexGrow: 1, p: { xs: 3, md: 4 }, minWidth: 0 }}>
            {children}

            {/* Footer actions */}
            {(onSkip || onContinue || onBack) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                justifyContent="flex-end"
                sx={{ mt: 4, pt: 3, borderTop: '1px solid #eef1f4', '& .MuiButton-root': { minHeight: 48, width: { xs: '100%', sm: 'auto' } } }}
              >
                {onBack && (
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    sx={{ textTransform: 'none', borderColor: '#d7dde3', color: '#5b6770', px: 4, height: 48, borderRadius: 2 }}
                  >
                    Back
                  </Button>
                )}
                {onSkip && (
                  <Box sx={{ textAlign: 'center', maxWidth: { sm: 280 } }}>
                    <Button
                      onClick={onSkip}
                      variant="outlined"
                      sx={{ textTransform: 'none', borderColor: TEAL, color: TEAL, px: 4, height: 48, borderRadius: 2, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {skipLabel}
                    </Button>
                    {skipCaption && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#8a94a0', mt: 0.5 }}>
                        {skipCaption}
                      </Typography>
                    )}
                  </Box>
                )}
                {onContinue && (
                  <Button
                    onClick={onContinue}
                    disabled={continueDisabled || saving}
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      backgroundColor: TEAL,
                      '&:hover': { backgroundColor: '#1da8a3' },
                      px: 5,
                      height: 48,
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >
                    {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : continueLabel}
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        </Box>

        {/* Privacy note */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 3 }}>
          <LockOutlinedIcon sx={{ fontSize: 16, color: '#8a94a0' }} />
          <Typography variant="caption" sx={{ color: '#8a94a0' }}>
            {privacyNote}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default OnboardingLayout;
